import React, { useEffect, useRef, useState } from "react";
import "./EnterMobileNumber.css";
import { useDispatch } from "react-redux";
import { setActiveComponent } from "../../../Redux/Slices/activeComponentSlice";
import { generateMobileOtp, signInWithPhoneNumber } from "../../../api/auth";
import Loader from "../../Loader";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate } from "react-router-dom";
import { getRedirectPath, clearRedirectPath } from "../../../utils/authRedirect";

function EnterMobileNumber() {
  const [phone, setPhone] = useState("");
  const [countryData, setCountryData] = useState(null);
  const [errorTooltip, setErrorTooltip] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpStage, setIsOtpStage] = useState(false);
  const otpLength = 6;
  const [otp, setOtp] = useState(new Array(otpLength).fill(""));
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    type: "info"
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Snackbar helper function
  const showSnackbar = (message, type = "info") => {
    setSnackbar({ show: true, message, type });
    setTimeout(() => {
      setSnackbar({ show: false, message: "", type: "info" });
    }, 3000);
  };

  const handleChange = (value, country) => {
    setPhone(value);
    setCountryData(country);
    if (errorTooltip) setErrorTooltip("");
  };

  const handleSendOtp = async () => {
    if (isLoading) return;

    const purePhoneNumber = phone.replace(/\D/g, "");

    if (!purePhoneNumber || purePhoneNumber.length < 10) {
      setErrorTooltip("Enter valid phone number");
      return;
    }

    setIsLoading(true);
    try {
      const otpResult = await generateMobileOtp(purePhoneNumber);
      console.log("otpResult", otpResult);
      if (otpResult?.success && otpResult?.statusCode === 200) {
        setIsOtpStage(true);
        setErrorTooltip("");
        showSnackbar("OTP sent successfully!", "success");
        // start timer
        setTimer(120);
        setCanResend(false);
        // clear previous OTP boxes
        setOtp(new Array(otpLength).fill(""));
        inputRefs.current.forEach((el) => el && (el.value = ""));
      } else {
        setErrorTooltip(otpResult?.message || "Failed to send OTP");
        showSnackbar(otpResult?.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send OTP";
      setErrorTooltip(message);
      showSnackbar(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (isLoading) return;
    const purePhoneNumber = phone.replace(/\D/g, "").slice(-10);
    const countryCode = countryData?.dialCode;

    const enteredOtp = Array.isArray(otp) ? otp.join("") : otp;
    if (!enteredOtp || enteredOtp.length < 4) {
      setErrorTooltip("Enter the OTP sent to your phone");
      showSnackbar("Please enter the OTP sent to your phone", "error");
      return;
    }

    setIsLoading(true);
    try {
      const result = await signInWithPhoneNumber(countryCode, purePhoneNumber, enteredOtp);
      console.log("result", result);
      if (result?.success && result?.statusCode === 200) {
        // optional persist for later flows
        localStorage.setItem("countryCode", countryCode || "");
        localStorage.setItem("phoneNumber", purePhoneNumber);
        showSnackbar("Login successful!", "success");
        const target = getRedirectPath();
        clearRedirectPath();
        navigate(target, { replace: true });
      } else {
        setErrorTooltip(result?.message || "OTP verification failed");
        showSnackbar(result?.message || "Login failed. Please try again.", "error");
      }
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Login failed. Please try again.";
      setErrorTooltip(message);
      showSnackbar(message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // OTP input handlers
  const handleOtpChange = (element, index) => {
    const value = (element.value || "").replace(/\D/, "");
    if (!value) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    // reflect sanitized value in the DOM input
    if (inputRefs.current[index]) inputRefs.current[index].value = value;
    if (index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const updated = [...otp];
      if (updated[index]) {
        // clear current box and its DOM value
        updated[index] = "";
        setOtp(updated);
        if (inputRefs.current[index]) inputRefs.current[index].value = "";
        return;
      }
      if (index > 0) {
        // move to previous, clear it, and update DOM
        inputRefs.current[index - 1]?.focus();
        const prevUpdated = [...otp];
        prevUpdated[index - 1] = "";
        setOtp(prevUpdated);
        if (inputRefs.current[index - 1]) inputRefs.current[index - 1].value = "";
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < otpLength - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter") {
      if (isOtpStage) handleVerifyOtp();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, otpLength);
    if (!paste) return;
    const chars = paste.split("");
    const updated = new Array(otpLength).fill("");
    for (let i = 0; i < chars.length; i++) {
      updated[i] = chars[i];
      if (inputRefs.current[i]) inputRefs.current[i].value = chars[i];
    }
    setOtp(updated);
    const nextIndex = Math.min(chars.length, otpLength - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  // resend logic
  const handleResend = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    try {
      const purePhoneNumber = phone.replace(/\D/g, "");
      const otpResult = await generateMobileOtp(purePhoneNumber);
      if (otpResult?.success && otpResult?.statusCode === 200) {
        showSnackbar("OTP has been resent successfully!", "success");
        setTimer(120);
        setCanResend(false);
        setOtp(new Array(otpLength).fill(""));
        inputRefs.current.forEach((el) => el && (el.value = ""));
        inputRefs.current[0]?.focus();
      } else {
        showSnackbar(otpResult?.message || "Failed to resend OTP", "error");
      }
    } catch (error) {
      showSnackbar(error?.response?.data?.message || error?.message || "Failed to resend OTP", "error");
    } finally {
      setIsResending(false);
    }
  };

  // countdown effect
  useEffect(() => {
    if (!isOtpStage) return;
    if (timer <= 0) {
      setCanResend(true);
      return;
    }
    const id = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [isOtpStage, timer]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOtpStage) {
        handleVerifyOtp();
      } else {
        handleSendOtp();
      }
    }
  };

  return (
    <div className="CreateNewAccount">
      <div className="createNewAccountContainer">
        <h1 className="getStartedText">Sign In with Mobile</h1>
        <p className="signInText2">Enter your phone to receive an OTP</p>
        <div className="generalInputContainer">
          <label htmlFor="phoneInput" className="labelText">
            Phone Number
          </label>
          <div className="phoneNumberInputContainer">
            <PhoneInput
              className="phoneInput"
              containerClass="containerClass"
              country={"ca"}
              value={phone}
              onChange={handleChange}
              enableSearch
              searchPlaceholder="Search country or code..."
              searchNotFound="No country found"
              inputProps={{
                name: "phone",
                required: true,
                autoFocus: true,
                onKeyDown: handleKeyDown,
                placeholder: "Enter your phone number",
                "aria-label": "Phone number input",
                "aria-describedby": errorTooltip ? "phone-error" : undefined,
                "aria-expanded": "false",
                "aria-haspopup": "listbox",
              }}
              inputStyle={{
                width: "100%",
                paddingLeft: "70px",
              }}
              containerStyle={{
                width: "100%",
                position: "relative",
              }}
              buttonStyle={{
                width: "58px",
              }}
              dropdownStyle={{
                width: "100%",
                maxHeight: "290px",
              }}
              searchStyle={{
                width: "100%",
              }}
              buttonClass="custom-flag-button"
              searchClass="search-dark"
              dropdownClass="country-dropdown"
              countryListProps={{
                role: "listbox",
                "aria-label": "Country selection",
              }}
            />
            {errorTooltip && (
              <span
                className="errorTooltip"
                id="phone-error"
                role="alert"
                aria-live="polite"
              >
                {errorTooltip}
              </span>
            )}
          </div>
        </div>

        {isOtpStage && (
          <div className="generalInputContainer" style={{ marginTop: "12px", width: "100%" }}>
            <label className="labelText">Phone Verification Code</label>
            <div
              className="otpInputContainer"
              onPaste={handleOtpPaste}
              style={{ display: "flex", gap: 8, marginTop: 10 }}
            >
              {Array.from({ length: otpLength }).map((_, i) => (
                <input
                  key={i}
                  type="text"
                  maxLength="1"
                  className={"otpInputTrue"}
                  ref={(el) => (inputRefs.current[i] = el)}
                  onChange={(e) => handleOtpChange(e.target, i)}
                  onKeyDown={(e) => handleOtpKeyDown(e, i)}
                  onFocus={(e) => e.target.select?.()}
                  style={{
                    all: "unset",
                    border: "2px solid #333",
                    height: "56px",
                    width: "56px",
                    borderRadius: "12px",
                    boxSizing: "border-box",
                    textAlign: "center",
                    fontSize: "18px",
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)",
                    color: "white",
                  }}
                />
              ))}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                marginTop: 12,
              }}
            >
              <span className="forgetMobilePasswordTimer" style={{ letterSpacing: 1 }}>
                {Math.floor(Math.max(timer, 0) / 60).toString().padStart(2, "0")}:{(Math.max(timer, 0) % 60).toString().padStart(2, "0")}
              </span>
              <button
                className={`resendBtn-${canResend && !isResending ? "active" : "disabled"}`}
                onClick={handleResend}
                disabled={!canResend || isResending}
                style={{
                  all: "unset",
                  cursor: canResend && !isResending ? "pointer" : "not-allowed",
                  color: canResend && !isResending ? "#48d1cc" : "#888",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                }}
                aria-label="Resend OTP"
              >
                {isResending ? "Resending..." : "Resend"}
              </button>
            </div>
          </div>
        )}

        <button
          className="btnContainer"
          onClick={isOtpStage ? handleVerifyOtp : handleSendOtp}
          disabled={isLoading}
          aria-label={
            isLoading ? "Processing..." : isOtpStage ? "Verify OTP" : "Send OTP"
          }
        >
          {isLoading ? <Loader /> : (
            <span className="btnText">{isOtpStage ? "Verify & Continue" : "Next"}</span>
          )}
        </button>
      </div>

      {/* Snackbar */}
      {snackbar.show && (
        <div className={`snackbar snackbar-${snackbar.type}`}>
          <span className="snackbar-message">{snackbar.message}</span>
        </div>
      )}
    </div>
  );
}

export default EnterMobileNumber;


